import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeftIcon, EyeIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

import { Button, Card, Spinner, Input } from '@realvisor/ui';
import { VALUO_VARIANT_OPTIONS } from '@realvisor/shared';
import { useForm, useUpdateForm } from '@/hooks';

// Předdefinované barvy
const PRESET_COLORS = [
  { name: 'Zelená', value: '#10b981' },
  { name: 'Modrá', value: '#2563eb' },
  { name: 'Fialová', value: '#7c3aed' },
  { name: 'Růžová', value: '#ec4899' },
  { name: 'Oranžová', value: '#f59e0b' },
  { name: 'Červená', value: '#ef4444' },
  { name: 'Tyrkysová', value: '#06b6d4' },
  { name: 'Šedá', value: '#6b7280' },
];

// Varianty formuláře — sdílený zdroj pravdy, stejný jako servíruje API
const VARIANTS = VALUO_VARIANT_OPTIONS;

export default function FormTheme() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: form, isLoading } = useForm(id || '');
  const updateForm = useUpdateForm();

  const [themeData, setThemeData] = useState({
    primaryColor: '#10b981',
    borderRadius: '12px',
    variant: 'complete',
    title: '',
    subtitle: '',
    submitButton: '',
  });

  // Načíst aktuální nastavení z config
  useEffect(() => {
    if (form?.config) {
      const config = form.config as any;
      setThemeData({
        primaryColor: config.theme?.primaryColor || '#10b981',
        borderRadius: config.theme?.borderRadius || '12px',
        variant: config.variant || 'complete',
        title: config.labels?.title || '',
        subtitle: config.labels?.subtitle || '',
        submitButton: config.labels?.submitButton || '',
      });
    }
  }, [form]);

  const handleSave = async () => {
    if (!id || !form) return;

    try {
      const currentConfig = (form.config as any) || {};
      
      const newConfig = {
        ...currentConfig,
        variant: themeData.variant,
        theme: {
          ...currentConfig.theme,
          primaryColor: themeData.primaryColor,
          borderRadius: themeData.borderRadius,
        },
        labels: {
          ...currentConfig.labels,
          title: themeData.title || undefined,
          subtitle: themeData.subtitle || undefined,
          submitButton: themeData.submitButton || undefined,
        },
      };

      await updateForm.mutateAsync({
        id,
        data: { config: newConfig },
      });

      toast.success('Vzhled uložen');
    } catch (error) {
      toast.error('Nepodařilo se uložit vzhled');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!form) {
    return (
      <Card padding="lg">
        <div className="text-center py-8">
          <h3 className="text-sm font-medium text-gray-900">Formulář nenalezen</h3>
          <Link to="/forms" className="mt-2 text-sm text-primary-600 hover:text-primary-700">
            Zpět na seznam
          </Link>
        </div>
      </Card>
    );
  }

  const formConfig = form.config as any;
  const formType = formConfig?.formType;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to={`/forms/${id}`}>
            <Button variant="outline" size="sm">
              <ArrowLeftIcon className="h-4 w-4" />
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Vzhled formuláře</h1>
            <p className="text-sm text-gray-500">{form.name}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Link to={`/forms/${id}/preview`}>
            <Button variant="outline" leftIcon={<EyeIcon className="h-4 w-4" />}>
              Náhled
            </Button>
          </Link>
          <Button onClick={handleSave} disabled={updateForm.isPending}>
            {updateForm.isPending ? 'Ukládám...' : 'Uložit změny'}
          </Button>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Nastavení */}
        <div className="space-y-6">
          {/* Varianta */}
          <Card padding="md">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Varianta formuláře</h2>
            <div className="grid grid-cols-2 gap-3">
              {VARIANTS.map((variant) => (
                <button
                  key={variant.value}
                  type="button"
                  onClick={() => setThemeData({ ...themeData, variant: variant.value })}
                  className={`p-3 rounded-lg border-2 text-left transition-all ${
                    themeData.variant === variant.value
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="font-medium text-sm text-gray-900">{variant.label}</div>
                  <div className="text-xs text-gray-500 mt-0.5">{variant.description}</div>
                </button>
              ))}
            </div>
          </Card>

          {/* Barvy */}
          <Card padding="md">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Barva</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Hlavní barva
                </label>
                <div className="flex flex-wrap gap-2">
                  {PRESET_COLORS.map((color) => (
                    <button
                      key={color.value}
                      type="button"
                      onClick={() => setThemeData({ ...themeData, primaryColor: color.value })}
                      className={`w-10 h-10 rounded-lg border-2 transition-all ${
                        themeData.primaryColor === color.value
                          ? 'border-gray-900 scale-110'
                          : 'border-transparent hover:scale-105'
                      }`}
                      style={{ backgroundColor: color.value }}
                      title={color.name}
                    />
                  ))}
                </div>
                <div className="mt-3 flex items-center gap-2">
                  <input
                    type="color"
                    value={themeData.primaryColor}
                    onChange={(e) => setThemeData({ ...themeData, primaryColor: e.target.value })}
                    className="w-10 h-10 rounded cursor-pointer"
                  />
                  <Input
                    value={themeData.primaryColor}
                    onChange={(e) => setThemeData({ ...themeData, primaryColor: e.target.value })}
                    placeholder="#10b981"
                    className="w-32"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Zaoblení rohů
                </label>
                <div className="flex gap-2">
                  {['0px', '8px', '12px', '16px', '24px'].map((radius) => (
                    <button
                      key={radius}
                      type="button"
                      onClick={() => setThemeData({ ...themeData, borderRadius: radius })}
                      className={`px-3 py-2 rounded border text-sm ${
                        themeData.borderRadius === radius
                          ? 'border-primary-500 bg-primary-50 text-primary-700'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      {radius}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </Card>

          {/* Texty */}
          <Card padding="md">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Texty</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nadpis
                </label>
                <Input
                  value={themeData.title}
                  onChange={(e) => setThemeData({ ...themeData, title: e.target.value })}
                  placeholder={formType === 'valuo' ? 'Odhad ceny nemovitosti' : 'Hypoteční kalkulačka'}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Podnadpis
                </label>
                <Input
                  value={themeData.subtitle}
                  onChange={(e) => setThemeData({ ...themeData, subtitle: e.target.value })}
                  placeholder={formType === 'valuo' ? 'Získejte okamžitý odhad tržní ceny' : 'Spočítejte si měsíční splátku'}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Text tlačítka
                </label>
                <Input
                  value={themeData.submitButton}
                  onChange={(e) => setThemeData({ ...themeData, submitButton: e.target.value })}
                  placeholder={formType === 'valuo' ? 'Zjistit cenu' : 'Spočítat'}
                />
              </div>
            </div>
          </Card>
        </div>

        {/* Náhled */}
        <div>
          <Card padding="md" className="sticky top-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Náhled</h2>
            
            <div 
              className="bg-gray-100 rounded-lg p-4"
              style={{ minHeight: '400px' }}
            >
              {/* Mini preview */}
              <div 
                className="bg-white shadow-lg overflow-hidden mx-auto"
                style={{ 
                  maxWidth: '320px',
                  borderRadius: themeData.borderRadius,
                }}
              >
                {/* Header */}
                <div 
                  className="p-4 text-white"
                  style={{ 
                    background: `linear-gradient(135deg, ${themeData.primaryColor}, ${themeData.primaryColor}dd)` 
                  }}
                >
                  <h3 className="font-semibold text-base">
                    {themeData.title || (formType === 'valuo' ? 'Odhad ceny nemovitosti' : 'Hypoteční kalkulačka')}
                  </h3>
                  <p className="text-sm opacity-90 mt-1">
                    {themeData.subtitle || (formType === 'valuo' ? 'Získejte okamžitý odhad tržní ceny' : 'Spočítejte si měsíční splátku')}
                  </p>
                </div>
                
                {/* Content placeholder */}
                <div className="p-4 space-y-3">
                  <div className="h-8 bg-gray-100 rounded" style={{ borderRadius: `calc(${themeData.borderRadius} / 2)` }} />
                  <div className="h-8 bg-gray-100 rounded" style={{ borderRadius: `calc(${themeData.borderRadius} / 2)` }} />
                  <div className="h-8 bg-gray-100 rounded" style={{ borderRadius: `calc(${themeData.borderRadius} / 2)` }} />
                  
                  <button
                    className="w-full py-2 text-white font-medium text-sm"
                    style={{ 
                      backgroundColor: themeData.primaryColor,
                      borderRadius: `calc(${themeData.borderRadius} / 2)`,
                    }}
                  >
                    {themeData.submitButton || (formType === 'valuo' ? 'Zjistit cenu' : 'Spočítat')}
                  </button>
                </div>
              </div>
            </div>
            
            <p className="text-xs text-gray-500 mt-3 text-center">
              Toto je zjednodušený náhled. Pro plný náhled použijte tlačítko "Náhled".
            </p>
          </Card>
        </div>
      </div>
    </div>
  );
}
